import { useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  FySelect,
  GRAIN_OPTIONS,
  KByTerritory,
  ProductSelect,
  ScopeScoreboard,
  Toggle,
  useHubData,
  useHubFilters,
} from "./shared";
import { TerritoryGuardrails } from "./territory-guardrails";

/**
 * With `territoryKey` the page is locked to that one territory (its own sidebar
 * link); without it, pills let you flip between territories and the company.
 */
export function ConsultantTerritoriesPage({ territoryKey }: { territoryKey?: string }) {
  const { grain, setGrain, fy, setFy, product, setProduct } = useHubFilters();
  const [pickedScope, setScopeKey] = useState("all");
  const scopeKey = territoryKey ?? pickedScope;
  const { data, error, loading } = useHubData(grain, fy, product);

  const scope = data?.scopes.find((s) => s.key === scopeKey) ?? data?.scopes[0];
  const series = data && scope ? (data.data[scope.key] ?? []) : [];
  const total = data && scope ? (data.totals[scope.key] ?? null) : null;
  const isDivision = scope?.kind === "division";
  // Division pages compare their territories on the year-to-date total (annual
  // view: the latest fiscal year shown).
  const lastIdx = data ? data.periods.filter((p) => p.status !== "future").length - 1 : -1;
  const compareFor = (key: string) =>
    data ? (data.totals[key] ?? data.data[key]?.[lastIdx] ?? null) : null;
  const compareLabel =
    data && grain === "year" ? (data.periods[lastIdx]?.label ?? "") : "year to date";

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          !territoryKey
            ? "Consultants — All territories"
            : isDivision
              ? (scope?.label ?? "Division")
              : `${scope?.label ?? "Territory"} — Territory`
        }
        description={
          territoryKey && isDivision && scope
            ? `Led by ${scope.divisionHead} — goals and every number for the division's ${scope.territories?.length ?? 0} territories combined, monthly, quarterly or annually.`
            : territoryKey && scope?.consultant
              ? `${scope.consultant} · ${scope.division} division — goals and every number for this territory, monthly, quarterly or annually.`
              : "Each territory's opportunities, closed deals, sales and K — monthly, quarterly or annually."
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Toggle value={grain} options={GRAIN_OPTIONS} onChange={setGrain} />
        {grain !== "year" && <FySelect data={data} fy={fy} onChange={setFy} />}
        <ProductSelect data={data} value={product} onChange={setProduct} />
      </div>

      {data && !territoryKey && (
        <div className="flex flex-wrap gap-2">
          {data.scopes.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setScopeKey(s.key)}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors",
                scope?.key === s.key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {error}
          </CardContent>
        </Card>
      )}
      {loading && !data && <Skeleton className="h-96 w-full" />}

      {data && scope && (
        <TerritoryGuardrails
          data={data}
          scope={scope}
          series={series}
          total={total}
          className={cn(loading && "opacity-60")}
        />
      )}

      {data && scope && isDivision && (
        <Card className={cn(loading && "opacity-60")}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Territories in this division{" "}
              <span className="font-normal text-muted-foreground">({compareLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <ScopeScoreboard
              rows={(scope.territories ?? []).flatMap((key) => {
                const t = data.scopes.find((x) => x.key === key);
                return t ? [{ scope: t, level: "territory" as const }] : [];
              })}
              metricsFor={compareFor}
              total={compareFor(scope.key)}
              totalLabel={`${scope.label} total`}
            />
          </CardContent>
        </Card>
      )}

      {data && scope && isDivision && (
        <KByTerritory
          rows={(scope.territories ?? []).flatMap((key) => {
            const t = data.scopes.find((x) => x.key === key);
            return t ? [{ scope: t, level: "territory" as const }] : [];
          })}
          metricsFor={compareFor}
          total={compareFor(scope.key)}
          totalLabel={`${scope.label} total`}
          asOfLabel={compareLabel}
          className={cn(loading && "opacity-60")}
        />
      )}

      {scope?.states && (
        <Card>
          <CardContent className="pt-6">
            <p className="mb-3 text-sm font-medium">
              States in this {isDivision ? "division" : "territory"}{" "}
              <span className="font-normal text-muted-foreground">({scope.states.length})</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {[...scope.states].sort().map((st) => (
                <span key={st} className="rounded-full border bg-muted/40 px-3 py-1 text-sm">
                  {st}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data && (
        <p className="text-xs text-muted-foreground">
          {data.meta.locatedPct}% of closed deals in this view have a customer state on file.
        </p>
      )}
    </div>
  );
}
